import { z } from "zod";
import {
  DATA_FORMAT_TYPES,
  CONDITION_VALIDATION_TYPES,
  DATA_TYPES_FOR_DATA_EXTRACTION,
  OPERATORS,
  DELAY_UNITS,
  EXCEPTION_ACTIONS,
  DATA_CONDITION_MODES,
  ALERT_MODES,
  ALERT_SEVERITIES,
  ALERT_ACTIONS,
  SCHEDULE_REFERENCE_POINTS,
  MONTHLY_CONFIG_TYPES,
  FREQUENCY_CONFIG_TYPES,
  PHASE_TYPES,
  MESSAGE_FREQUENCIES,
} from "@/isomorphic/constants/workflow-phase-consts";
import { KnowledgeBaseSchema } from "@/isomorphic/schemas/knowledge-base";
// ============================================================================
// SHARED SCHEMAS (used by both frontend and backend)
// ============================================================================

export const DataFormatSchema = z.object({
  type: z.enum(DATA_FORMAT_TYPES),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  unit: z.string().optional(),
  scaleMinLabel: z.string().optional(),
  scaleMaxLabel: z.string().optional(),
  scaleLabels: z.record(z.string(), z.string()).optional(),
  mcqOptions: z.array(z.string()).optional(),
  multipleChoiceAllowed: z.boolean().optional(),
});

export const FrequencyDataConditionSchema = z.object({
  validationCount: z.number().optional(),
  validationType: z.enum(CONDITION_VALIDATION_TYPES).optional(),
  timeWindow: z.number().optional(),
});

export const DataConditionSchema = FrequencyDataConditionSchema.extend({
  dataReferenceId: z.string(),
  dataReferenceType: z.enum(DATA_TYPES_FOR_DATA_EXTRACTION),
  operator: z.enum(OPERATORS),
  threshold: z.number().optional(),
  thresholdMin: z.number().optional(),
  thresholdMax: z.number().optional(),
  targetedValues: z.array(z.union([z.string(), z.boolean()])).optional(),
});

export const TimeConditionSchema = z.object({
  description: z.string().optional(),
  delaybeforeNextPhase: z.number(),
  delayUnit: z.enum(DELAY_UNITS),
});

export const ExceptionSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  action: z.enum(EXCEPTION_ACTIONS),
  delayDuration: z.number().optional(),
  dataConditions: z.array(DataConditionSchema),
});

export const AdvancementConditionSchema = z.object({
  id: z.string(),
  name: z.string(),
  mode: z.enum(DATA_CONDITION_MODES),
  timeCondition: TimeConditionSchema.optional(),
  dataCondition: DataConditionSchema.optional(),
  exceptions: z.array(ExceptionSchema),
  priority: z.number().optional(),
  isActive: z.boolean().optional(),
});

export const AlertSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  mode: z.enum(ALERT_MODES),
  severity: z.enum(ALERT_SEVERITIES),
  dataConditions: z.array(DataConditionSchema).optional(),
  message: z.string(),
  actions: z.array(z.enum(ALERT_ACTIONS)),
  isActive: z.boolean(),
});

export const ScheduledCallSchema = z.object({ dayOffset: z.number() });
export const ScheduledCallsConfigSchema = z.object({
  referencePoint: z.enum(SCHEDULE_REFERENCE_POINTS),
  calls: z.array(ScheduledCallSchema),
});
export const DailyConfigSchema = z.object({ daysOfWeek: z.array(z.number()) });
export const WeeklyConfigSchema = z.object({ dayOfWeek: z.number() });
export const MonthlyConfigSchema = z.object({
  type: z.enum(MONTHLY_CONFIG_TYPES),
  dayOfMonth: z.number().optional(),
});
export const CustomConfigSchema = z.object({ intervalDays: z.number() });

export const CallsConfigurationSchema = z.object({
  duration: z.number(),
  frequency: z.enum(FREQUENCY_CONFIG_TYPES),
  scheduledCalls: ScheduledCallsConfigSchema.optional(),
  dailyConfig: DailyConfigSchema.optional(),
  weeklyConfig: WeeklyConfigSchema.optional(),
  monthlyConfig: MonthlyConfigSchema.optional(),
  customConfig: CustomConfigSchema.optional(),
});

const BasePhaseInformationSchema = z.object({
  id: z.string(),
  type: z.enum(PHASE_TYPES),
  name: z.string(),
  order: z.number().optional(),
  followsDataId: z.string().optional(),
  conditions: z.array(DataConditionSchema).optional(),
});

export const CollectedDataSchema = BasePhaseInformationSchema.extend({
  type: z.literal("collected_data"),
  format: DataFormatSchema,
  question: z.string().optional(),
});

export const CalculatedDataSchema = BasePhaseInformationSchema.extend({
  type: z.literal("calculated_data"),
  formula: z.string(),
  unit: z.string().optional(),
  question: z.string().optional(),
  description: z.string().optional(),
});

export const TransmittedMessageSchema = BasePhaseInformationSchema.extend({
  type: z.literal("transmitted_data"),
  content: z.string(),
  beforeNextDataConstraint: z.boolean(),
  afterPreviousDataConstraint: z.boolean(),
  frequency: z.enum(MESSAGE_FREQUENCIES),
});

export const CallDataSchema = z.union([CollectedDataSchema, CalculatedDataSchema, TransmittedMessageSchema]);

// ============================================================================
// BASE PHASE SCHEMA (matches backend DbPhaseOutput)
// ============================================================================

// To sync with DbPhaseOutput in backend/schemas/phase.ts
const BasePhaseSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  workflowId: z.string(),
  knowledgeBaseId: z.string().nullable().optional(),
  creatorId: z.string(),

  // History recording
  historyVersion: z.number(),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),

  // Data fields
  phaseKey: z.string(),
  orderIndex: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  callsConfiguration: CallsConfigurationSchema.nullable().optional(),
  callDatas: z.array(CallDataSchema).nullable().optional(),
  advancementConditions: z.array(AdvancementConditionSchema).nullable().optional(),
  alerts: z.array(AlertSchema).nullable().optional(),
});

export const PhaseSchema = BasePhaseSchema;
export const PhaseWithKbSchema = BasePhaseSchema.extend({
  knowledgeBase: KnowledgeBaseSchema.optional(),
});

export const PhaseListItemSchema = BasePhaseSchema.extend({
  agentId: z.string().optional(),
  workflowHistoryVersion: z.number().optional(),
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  workflowName: z.string().nullable().optional(),
  knowledgeBaseName: z.string().nullable().optional(),
});

export const PhaseListSchema = z.array(PhaseListItemSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreatePhaseBodySchema = z.object({
  workflowId: z.string().uuid("Invalid workflow ID"),
  phaseKey: z.string().min(1, "Phase key is required"),
  orderIndex: z.number(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  knowledgeBaseId: z.string().uuid().nullable().optional(),
  callsConfiguration: CallsConfigurationSchema,
  callDatas: z.array(CallDataSchema).default([]),
  advancementConditions: z.array(AdvancementConditionSchema).default([]),
  alerts: z.array(AlertSchema).default([]),
});

export const LinkKnowledgeBaseBodySchema = z.object({
  knowledgeBaseId: z.string().uuid("Invalid knowledge base ID"),
});

export const UpdatePhaseBodySchema = z.object({
  historyVersion: z.number().int().positive(),
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  knowledgeBaseId: z.string().uuid().nullable().optional(),
  orderIndex: z.number().optional(),
  callsConfiguration: CallsConfigurationSchema.optional(),
  callDatas: z.array(CallDataSchema).optional(),
  advancementConditions: z.array(AdvancementConditionSchema).optional(),
  alerts: z.array(AlertSchema).optional(),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type DataFormat = z.infer<typeof DataFormatSchema>;
export type DataCondition = z.infer<typeof DataConditionSchema>;
export type TimeCondition = z.infer<typeof TimeConditionSchema>;
export type Exception = z.infer<typeof ExceptionSchema>;
export type AdvancementCondition = z.infer<typeof AdvancementConditionSchema>;
export type Alert = z.infer<typeof AlertSchema>;
export type CallsConfiguration = z.infer<typeof CallsConfigurationSchema>;
export type CallData = z.infer<typeof CallDataSchema>;
export type CollectedData = z.infer<typeof CollectedDataSchema>;
export type CalculatedData = z.infer<typeof CalculatedDataSchema>;
export type ScheduledCall = z.infer<typeof ScheduledCallSchema>;
export type ScheduledCallsConfig = z.infer<typeof ScheduledCallsConfigSchema>;
export type DailyConfig = z.infer<typeof DailyConfigSchema>;
export type WeeklyConfig = z.infer<typeof WeeklyConfigSchema>;
export type MonthlyConfig = z.infer<typeof MonthlyConfigSchema>;
export type CustomConfig = z.infer<typeof CustomConfigSchema>;
export type TransmittedMessage = z.infer<typeof TransmittedMessageSchema>;
export type Operator = (typeof OPERATORS)[number];
export type AlertAction = (typeof ALERT_ACTIONS)[number];
export type CreatePhaseBody = z.infer<typeof CreatePhaseBodySchema>;
export type UpdatePhaseBody = z.infer<typeof UpdatePhaseBodySchema>;
export type LinkKnowledgeBaseBody = z.infer<typeof LinkKnowledgeBaseBodySchema>;

// API Response types
export type CreatePhaseApiResponse = Phase;
export type UpdatePhaseApiResponse = Phase;
export type PhaseListApiResponse = z.infer<typeof PhaseListSchema>;
export type LinkKnowledgeBaseApiResponse = { knowledgeBaseId: string };
export type UnlinkKnowledgeBaseApiResponse = { success: boolean };

export type Phase = z.infer<typeof PhaseSchema>;
export type PhaseListItem = z.infer<typeof PhaseListItemSchema>;
export type PhaseWithKb = z.infer<typeof PhaseWithKbSchema>;
